/**
 * Renders demo-imported photos as numbered markers and the demo flight path
 * as a polyline on the shared map. Uses isolated `dock-demo-*` Mapbox sources
 * so it can never collide with existing GeoPhoto / FlightPath layers.
 *
 * Click on a photo marker → opens the demo photo viewer (DemoPhotoPopup).
 *
 * Lifecycle: mounted at EditorPage level alongside DockHomeLayer and
 * DockMissionRouteLayer so imported photos / flight path persist across
 * ticket switches. Self-healing styledata recovery — if external code wipes
 * our sources, we reinstall on the next styledata event rather than waiting
 * for the next style.load.
 */

import { useEffect } from 'react';
import type { Map as MapboxMap, MapMouseEvent } from 'mapbox-gl';
import { useMapStore } from '../../stores';
import { useDockDemoStore } from '../../stores/dockDemoStore';
import type { DemoPhoto, DemoFlightPath } from './types';

const SRC_PHOTOS = 'dock-demo-photos-src';
const SRC_FLIGHT = 'dock-demo-flight-src';
const LAYER_FLIGHT = 'dock-demo-flight-lyr';
const LAYER_PHOTOS_CIRCLE = 'dock-demo-photos-circle-lyr';
const LAYER_PHOTOS_LABEL = 'dock-demo-photos-label-lyr';

const ALL_LAYERS = [LAYER_FLIGHT, LAYER_PHOTOS_CIRCLE, LAYER_PHOTOS_LABEL] as const;
const ALL_SOURCES = [SRC_PHOTOS, SRC_FLIGHT] as const;

function isMissing(map: MapboxMap): boolean {
  for (const id of ALL_SOURCES) if (!map.getSource(id)) return true;
  for (const id of ALL_LAYERS) if (!map.getLayer(id)) return true;
  return false;
}

/**
 * Aggregate imported photos + flight path from missions whose
 * `importedDataVisible` is not false. JS-level filter — features for
 * hidden missions are simply omitted from setData rather than filtered
 * at the Mapbox layer level. Simpler and avoids whatever Mapbox-internal
 * issue was breaking the setFilter approach.
 *
 * Only the first matching flight path renders (matches the old behaviour
 * — multiple flight paths overlapping read as noise).
 */
function collectVisibleImported(missions: ReadonlyArray<import('./types').DemoMission>) {
  const photos: DemoPhoto[] = [];
  let flightPath: DemoFlightPath | null = null;
  for (const m of missions) {
    if (m.importedDataVisible === false) continue;
    if (m.importedPhotos) photos.push(...m.importedPhotos);
    if (!flightPath && m.importedFlightPath) flightPath = m.importedFlightPath;
  }
  return { photos, flightPath };
}

function buildPhotoFeatures(photos: DemoPhoto[]) {
  // Only thermal photos with valid GPS appear on the map.
  const valid = photos.filter(
    (p) => p.category === 'thermal_photo'
      && p.coordinates[0] !== 0
      && p.coordinates[1] !== 0,
  );
  return {
    type: 'FeatureCollection' as const,
    features: valid.map((p, idx) => ({
      type: 'Feature' as const,
      properties: { id: p.id, index: idx + 1, fileName: p.fileName },
      geometry: { type: 'Point' as const, coordinates: p.coordinates },
    })),
  };
}

function buildFlightFeatures(fp: DemoFlightPath | null) {
  if (!fp || fp.coordinates.length < 2) {
    return { type: 'FeatureCollection' as const, features: [] };
  }
  return {
    type: 'FeatureCollection' as const,
    features: [{
      type: 'Feature' as const,
      properties: { id: fp.id, name: fp.name },
      geometry: {
        type: 'LineString' as const,
        coordinates: fp.coordinates.map(([lng, lat]) => [lng, lat]),
      },
    }],
  };
}

function ensureSourcesAndLayers(map: MapboxMap): void {
  if (!map.getSource(SRC_PHOTOS)) {
    map.addSource(SRC_PHOTOS, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
  }
  if (!map.getSource(SRC_FLIGHT)) {
    map.addSource(SRC_FLIGHT, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
  }

  if (!map.getLayer(LAYER_FLIGHT)) {
    map.addLayer({
      id: LAYER_FLIGHT,
      type: 'line',
      source: SRC_FLIGHT,
      paint: {
        'line-color': '#fbbf24',
        'line-width': 3,
        'line-opacity': 0.85,
      },
    });
  }
  if (!map.getLayer(LAYER_PHOTOS_CIRCLE)) {
    map.addLayer({
      id: LAYER_PHOTOS_CIRCLE,
      type: 'circle',
      source: SRC_PHOTOS,
      paint: {
        'circle-radius': 9,
        'circle-color': '#dc2626',
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff',
      },
    });
  }
  if (!map.getLayer(LAYER_PHOTOS_LABEL)) {
    map.addLayer({
      id: LAYER_PHOTOS_LABEL,
      type: 'symbol',
      source: SRC_PHOTOS,
      layout: {
        'text-field': ['get', 'index'],
        'text-size': 11,
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: {
        'text-color': '#ffffff',
      },
    });
  }
}

/**
 * Bring our layers to the top of the map's layer stack — same idempotent
 * trick the waypoint hook uses. Iterating in render-stack order means the
 * label layer ends up topmost.
 */
function pinLayersToTop(map: MapboxMap): void {
  for (const id of [LAYER_FLIGHT, LAYER_PHOTOS_CIRCLE, LAYER_PHOTOS_LABEL]) {
    try {
      if (map.getLayer(id)) map.moveLayer(id);
    } catch {
      // ignore — layer may not exist yet
    }
  }
}


function teardownSourcesAndLayers(map: MapboxMap): void {
  for (const id of [LAYER_PHOTOS_LABEL, LAYER_PHOTOS_CIRCLE, LAYER_FLIGHT]) {
    try {
      if (map.getLayer(id)) map.removeLayer(id);
    } catch {
      // map mid-teardown — defensive
    }
  }
  for (const id of [SRC_PHOTOS, SRC_FLIGHT]) {
    try {
      if (map.getSource(id)) map.removeSource(id);
    } catch {
      // ignore
    }
  }
}

export default function DemoMissionMapLayer() {
  const map = useMapStore((s) => s.map);
  const isMapReady = useMapStore((s) => s.isMapReady);
  const setSelectedDemoPhoto = useDockDemoStore((s) => s.setSelectedDemoPhoto);

  // Lifecycle (install / teardown / style-load + styledata recovery) plus a
  // direct Zustand subscription that drives data updates. Deps deliberately
  // exclude `missions` so a data edit doesn't tear down + re-create every
  // layer.
  //
  // Visibility is governed by data presence, not by setLayoutProperty:
  // collectVisibleImported drops missions with importedDataVisible === false,
  // so toggling the data checkbox off yields an empty FeatureCollection and
  // nothing renders. Earlier attempts to use layer-level 'visibility'
  // produced sticky 'none' state when a no-op apply() stranded the layer.
  //
  // Reliability split: addSource / addLayer require the style to be fully
  // loaded (so ensureSourcesAndLayers gates on isStyleLoaded and falls back
  // to a `once('idle')` retry for first-mount layer creation), but setData
  // on an EXISTING source is always safe — it runs unconditionally with a
  // try/catch wrap. This matters because MapboxDraw's render-time crashes
  // (a known dev-mode artifact: `continuePlacement: undefined.get()`) can
  // permanently keep isStyleLoaded false and prevent 'idle' from firing.
  // Without the unconditional setData path, toggling the data checkbox in
  // a dev session would silently no-op forever after the first crash.
  useEffect(() => {
    if (!map || !isMapReady) return;
    let pendingIdle = false;
    const apply = () => {
      const styleLoaded = map.isStyleLoaded();
      if (styleLoaded) {
        ensureSourcesAndLayers(map);
      } else if (!pendingIdle) {
        // Schedule a retry so layers eventually get created on first mount.
        pendingIdle = true;
        map.once('idle', () => {
          pendingIdle = false;
          apply();
        });
      }
      const s = useDockDemoStore.getState();
      const { photos, flightPath } = collectVisibleImported(s.missions);
      const ph = map.getSource(SRC_PHOTOS);
      const fl = map.getSource(SRC_FLIGHT);
      if (ph && 'setData' in ph) {
        try { ph.setData(buildPhotoFeatures(photos)); } catch { /* style mid-mutation */ }
      }
      if (fl && 'setData' in fl) {
        try { fl.setData(buildFlightFeatures(flightPath)); } catch { /* same */ }
      }
      if (styleLoaded) {
        pinLayersToTop(map);
      }
    };

    const repin = () => {
      if (isMissing(map)) {
        apply();
      } else {
        pinLayersToTop(map);
      }
    };

    apply();
    map.on('style.load', apply);
    map.on('styledata', repin);

    let prevMissions = useDockDemoStore.getState().missions;
    let rafId: number | null = null;
    const queueApply = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        apply();
      });
    };
    const unsubscribe = useDockDemoStore.subscribe(() => {
      const s = useDockDemoStore.getState();
      if (s.missions !== prevMissions) {
        prevMissions = s.missions;
        queueApply();
      }
    });

    return () => {
      unsubscribe();
      if (rafId !== null) cancelAnimationFrame(rafId);
      map.off('style.load', apply);
      map.off('styledata', repin);
      teardownSourcesAndLayers(map);
    };
  }, [map, isMapReady]);

  // Click handler on photo markers — opens the demo photo viewer.
  useEffect(() => {
    if (!map || !isMapReady) return;
    const handler = (e: MapMouseEvent) => {
      const feature = e.features?.[0];
      const id = feature?.properties?.id;
      if (typeof id === 'string') setSelectedDemoPhoto(id);
    };
    const enter = () => { map.getCanvas().style.cursor = 'pointer'; };
    const leave = () => { map.getCanvas().style.cursor = ''; };
    map.on('click', LAYER_PHOTOS_CIRCLE, handler);
    map.on('mouseenter', LAYER_PHOTOS_CIRCLE, enter);
    map.on('mouseleave', LAYER_PHOTOS_CIRCLE, leave);
    return () => {
      map.off('click', LAYER_PHOTOS_CIRCLE, handler);
      map.off('mouseenter', LAYER_PHOTOS_CIRCLE, enter);
      map.off('mouseleave', LAYER_PHOTOS_CIRCLE, leave);
    };
  }, [map, isMapReady, setSelectedDemoPhoto]);

  return null;
}
